/*
 * Description:
 * ---------------------
 * Home Wi-Fi: how each device on the home network is getting on, the way the
 * person using it would say it (the television buffers, the game lags), and
 * where the trouble is: its Wi-Fi link, the path inside the home, or the
 * internet line. The box judges it (cloud-anchor/pkg/homewifi) from what each
 * device is doing and what its own radios and echoes see, with nothing
 * installed on the device. Where the box can act, a person asks it to; the
 * rest is advice. All wording lives in homeWifiWords.js.
 *
 * ! file components/HomeWifiPanel.js
 */
import React, { useCallback, useMemo, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import useEdgeFeature, { FEATURE_ABSENT, FEATURE_UNREACHABLE } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import useVisibleInterval from "../hooks/useVisibleInterval";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import HomeWifiMap from "./HomeWifiMap";
import { useConfirm } from "./ConfirmModal";
import { useSession } from "./PortalGate";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./homeWifiWords";
import "./DeviceLimits.css";
import "./DataBudgets.css";
import "./HomeWifi.css";

export default function HomeWifiPanel({ selectedNode }) {
  const { state, payload, refresh } = useEdgeFeature(selectedNode, "/wifi/home");
  useVisibleInterval(refresh, 15000, state === FEATURE_UNREACHABLE);
  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a box to see its home network.</div></div>;
  }
  if (state === FEATURE_ABSENT) {
    return <FeatureUnavailableNotice nodeId={selectedNode} feature="Home Wi-Fi" />;
  }
  if (state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read the home network from {selectedNode}</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    );
  }
  if (!payload) return <div className="panel"><div className="panel-sub">Reading the home network…</div></div>;
  // Keyed on the node, so one home's devices never show under another's name.
  return <Home key={selectedNode} selectedNode={selectedNode} first={payload} />;
}

function Home({ selectedNode, first }) {
  const url = useMemo(() => nodeApiUrlFor(selectedNode), [selectedNode]);
  const { canWrite } = useSession();
  const confirm = useConfirm();
  const [view, setView] = useState(first);
  const [lost, setLost] = useState(false);

  const read = useCallback(async () => {
    const res = await fetchWithTimeout(url("/wifi/home")).catch(() => null);
    const body = res?.ok ? await res.json().catch(() => null) : null;
    setLost(!body);
    if (body) setView(body);
  }, [url]);
  const { busy, error, send } = useEdgeWrite(selectedNode, read);
  useVisibleInterval(read, 5000);

  const devices = view.devices || [];
  const radios = view.radios || [];
  const active = devices.filter((d) => d.verdict?.experience !== "idle");
  const idle = devices.filter((d) => d.verdict?.experience === "idle");
  const head = W.headline(view);
  const act = async (d, r) => {
    const ask = r.code === "make_room" ? W.confirmRoom(d, r, devices) : W.confirmMove(d, r, radios);
    if (await confirm(ask)) await send("POST", "/wifi/home/fix", { device: d.mac, remedy: r.code });
  };
  const giveBack = (d) => send("POST", "/wifi/home/fix", { device: d.mac, remedy: "give_back" });
  const holding = (d) => (view.fixes || []).some((f) => f.remedy === "make_room" && f.device === d.mac && (f.state === "trying" || f.state === "kept"));

  return (
    <div className="dl hw">
      {error && <div className="dl-error" role="alert">{error}</div>}
      <div className={`panel db-headline hw-head-${head.tone}`} role="status">{head.text}</div>
      {lost && <p className="dl-note">The box did not answer the last read, so this may be out of date. The screen keeps trying.</p>}
      {(view.problems || []).map((p, i) => <p key={i} className="dl-note">{W.problemSentence(p)}</p>)}

      {active.length > 0 && (
        <div className="panel">
          <div className="panel-title">In use now</div>
          {active.map((d) => (
            <DeviceRow key={d.mac || d.ip} d={d} view={view} canWrite={canWrite} busy={busy} onAct={act}
              held={holding(d)} onGiveBack={giveBack} />
          ))}
        </div>
      )}

      {idle.length > 0 && (
        <details className="panel hw-idle">
          <summary>{idle.length} {idle.length === 1 ? "device" : "devices"} connected and not in use</summary>
          {idle.map((d) => <DeviceRow key={d.mac || d.ip} d={d} view={view} canWrite={canWrite} busy={busy} onAct={act} />)}
        </details>
      )}

      <HomeWifiMap selectedNode={selectedNode} />

      {radios.length > 0 && (
        <div className="panel">
          <div className="panel-title">{radios.some((r) => !r.device) ? "This box's Wi-Fi" : "The access points"}</div>
          {radios.map((r) => <p key={r.name} className="hw-line">{W.radioSentence(r)}</p>)}
        </div>
      )}

      {(view.fixes || []).length > 0 && (
        <div className="panel">
          <div className="panel-title">What the box did</div>
          {view.fixes.map((f) => <p key={f.id} className="hw-line">{W.fixSentence(f, clock(f.at))}</p>)}
        </div>
      )}

      {view.can_make_room && (
        <div className="panel">
          <div className="dl-head">
            <div className="panel-title">Making room</div>
            <label className="hw-mode">
              <span>Make room</span>
              <select className="dl-input" value={view.make_room || "ask"} disabled={!canWrite || busy}
                aria-label="When the box makes room"
                onChange={(e) => send("PUT", "/wifi/home/settings", { make_room: e.target.value })}>
                {W.ROOM_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
          </div>
          <p className="dl-note">{W.ROOM_EXPLAINED}</p>
        </div>
      )}
    </div>
  );
}

function DeviceRow({ d, view, canWrite, busy, onAct, held, onGiveBack }) {
  const devices = view.devices || [];
  const radios = view.radios || [];
  const v = d.verdict || {};
  const tone = W.TONE[v.experience] || "off";
  const idle = v.experience === "idle";
  // A device doing fine needs no reason and no advice; what the box could
  // not see is said only where it leaves a doubt.
  const troubled = v.experience === "poor" || v.experience === "fair" || v.experience === "unknown";
  const lead = W.leadCause(d, troubled);
  const actions = (v.remedies || []).filter((r) => r.action);
  const advice = troubled ? (v.remedies || []).filter((r) => !r.action) : [];
  const radio = radios.find((r) => r.name === d.station?.radio);
  const shown = idle
    ? (v.causes || []).filter((c) => c.severity !== "note")
    : (v.causes || []).filter((c) => c !== lead && c.severity !== "note");
  // Every reason but the one the row leads with, then the figures they rest on.
  const known = [...(idle ? [] : shown.map((c) => W.causeSentence(c, d, devices))), ...W.measurements(d, radio)];
  return (
    <div className={`hw-device hw-${tone}`}>
      <div className="dl-row">
        <span className={`hw-dot hw-${tone}`} aria-hidden="true" />
        <div className="dl-row-main">
          <strong>{W.deviceName(d)}</strong>
          <span>{W.doing(d.activity)} · {W.EXPERIENCE[v.experience] || v.experience}</span>
        </div>
        {canWrite && actions.map((r) => (
          <button key={r.code} type="button" className="dl-btn primary" disabled={busy} onClick={() => onAct(d, r)}>
            {W.actionLabel(r, radios)}
          </button>
        ))}
        {canWrite && held && (
          <button type="button" className="dl-btn" disabled={busy} onClick={() => onGiveBack(d)}>{W.GIVE_BACK}</button>
        )}
      </div>
      {!idle && lead && <p className="hw-why">{W.causeSentence(lead, d, devices)}</p>}
      {!idle && <Path d={d} outside={view.outside} radio={radio} />}
      {!idle && W.WHERE[v.where] && <p className="hw-where">{W.WHERE[v.where]}</p>}
      {idle && shown.length > 0 && <p className="hw-why">When it is used: {W.causeSentence(shown[0], d, devices)}</p>}
      {!idle && actions.map((r) => <p key={r.code} className="hw-also">{W.remedySentence(r, d, devices, radios)}</p>)}
      {!idle && advice.length > 0 && (
        <ul className="hw-advice">
          {advice.map((r) => <li key={r.code + (r.target || "")}>{W.remedySentence(r, d, devices, radios)}</li>)}
        </ul>
      )}
      {known.length > 0 && (
        <details className="dl-measure">
          <summary>How the box knows</summary>
          {known.map((m, i) => <span key={i}>{m}</span>)}
        </details>
      )}
    </div>
  );
}

/* The path a device's traffic takes, read left to right: the device, its
   link to this box, this box, the internet line, the internet. The segment
   at fault carries the verdict's colour; one the box cannot see is grey and
   dashed, never green. */
function Path({ d, outside, radio }) {
  const where = d.verdict?.where;
  const tone = W.TONE[d.verdict?.experience] || "off";
  const seen = !!d.station || d.timing?.inside?.samples >= 20 ||
    (d.latency?.answered >= 5 && (d.activity?.kind === "call" || d.activity?.kind === "live"));
  const homeTone = where === "wifi" || where === "home" ? tone : seen ? "ok" : "off";
  const lineTone = where === "internet" ? tone
    : { good: "ok", degraded: "warn", down: "bad" }[outside?.state] || "off";
  const homeLabel = d.station ? `Wi-Fi${radio ? `, ${radio.band}` : ""}` : "Wi-Fi or a cable";
  return (
    <div className="hw-path" aria-label="Where its traffic goes">
      <span className="hw-node">{W.deviceName(d)}</span>
      <span className={`hw-seg hw-${homeTone}`}><i /><em>{homeLabel}</em></span>
      <span className="hw-node">This box</span>
      <span className={`hw-seg hw-${lineTone}`}><i /><em>internet line</em></span>
      <span className="hw-node">Internet</span>
    </div>
  );
}

function clock(at) {
  const t = new Date(at);
  return Number.isNaN(t.getTime()) ? "" : t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
